/**
 * BlogDataContext — provides server-prefetched blog data to components.
 * During SSR, the server passes data directly via props.
 * During client hydration, the data is read from the __BLOG_DATA__ script tag.
 * After hydration, tRPC queries take over for navigation.
 */
import { createContext, useContext, type ReactNode } from "react";

export type BlogListItem = {
  id: number;
  slug: string;
  locale: string;
  title: string;
  metaDescription: string;
  targetQueries: string;
  publishedAt: string | Date;
};

export type BlogListData = {
  type: "list";
  locale: string;
  items: BlogListItem[];
  total: number;
};

export type BlogArticleData = {
  type: "article";
  locale: string;
  slug: string;
  article: {
    id: number;
    slug: string;
    locale: string;
    title: string;
    metaDescription: string;
    targetQueries: string;
    content: string;
    publishedAt: string | Date;
    createdAt: string | Date;
  };
  availableLocales: string[];
};

export type BlogPrefetchData = BlogListData | BlogArticleData | null;

const BlogDataContext = createContext<BlogPrefetchData>(null);

export function BlogDataProvider({
  data,
  children,
}: {
  data: BlogPrefetchData;
  children: ReactNode;
}) {
  return (
    <BlogDataContext.Provider value={data}>{children}</BlogDataContext.Provider>
  );
}

export function useBlogPrefetchData(): BlogPrefetchData {
  return useContext(BlogDataContext);
}

/**
 * Read the __BLOG_DATA__ script tag injected by the server.
 * Returns null if not found (e.g., client-side navigation to blog).
 */
export function readBlogDataFromDOM(): BlogPrefetchData {
  if (typeof document === "undefined") return null;
  const script = document.getElementById("__BLOG_DATA__");
  if (!script) return null;
  try {
    return JSON.parse(script.textContent || "null");
  } catch {
    return null;
  }
}
